/**
 * Thin client for the SUDO API.
 *
 * Hand-written rather than generated because it owns two things worth
 * controlling directly: cookie credentials and CSRF. The session cookie is
 * HttpOnly, so nothing here can read or forge it — that is the point.
 */

// Configured at build time so the same source deploys against a local API, a
// staging host or production without a code change.
const API_BASE = import.meta.env.VITE_API_BASE ?? "https://api.sudogamesservers.co.uk";

export type HealthStatus = "Healthy" | "Degraded" | "Unhealthy" | "Unreachable";

export interface HealthReport {
  status: HealthStatus;
  checks?: Array<{
    name: string;
    status: HealthStatus;
    durationMs: number;
    description: string | null;
    error: string | null;
    tags: string[];
  }>;
}

export interface Membership {
  orgId: string;
  slug: string;
  name: string;
  role: string;
  permissions: string[];
}

export interface Me {
  authenticated: boolean;
  userId: string | null;
  email: string | null;
  displayName: string | null;
  emailConfirmed: boolean;
  twoFactorEnabled: boolean;
  memberships: Membership[];
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly problems?: Record<string, string[]>,
    /**
     * The RFC 9457 `type` URI, when the API answered with a problem document.
     *
     * This is the stable machine-readable identity of a refusal, and it is the
     * only thing that separates the two 402s from one another: "you are out of
     * servers" and "your plan does not include this" share a status code and
     * differ in what the page should offer next.
     */
    public readonly problemType?: string,
    /** Problem extension members, keyed as the API sent them. */
    public readonly extensions?: Readonly<Record<string, unknown>>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Identifies the "your plan includes N servers and you have N" refusal. */
export const SERVER_LIMIT_REACHED = "https://sudogamesservers.co.uk/problems/server-limit-reached";

/** Identifies the "your plan does not grant this capability" refusal. */
export const FEATURE_NOT_IN_PLAN = "https://sudogamesservers.co.uk/problems/feature-not-in-plan";

/**
 * Whether a refusal was about money rather than about permission.
 *
 * 402 and 403 are answers to different questions and have different remedies:
 * 403 says this person's role does not allow it, and somebody changing a role
 * fixes it; 402 says this organisation's plan does not include it, and no role
 * change ever will. A view that renders one banner for both sends half its users
 * to the wrong place, so the split is made explicit here rather than left to
 * whoever remembers to compare a number.
 */
export function isPlanRefusal(caught: unknown): caught is ApiError {
  return caught instanceof ApiError && caught.status === 402;
}

/** Whether a refusal was about the caller's role. */
export function isPermissionRefusal(caught: unknown): caught is ApiError {
  return caught instanceof ApiError && caught.status === 403;
}

export interface ServerLimitReached {
  plan: string;
  limit: number;
  used: number;
}

export interface FeatureNotInPlan {
  plan: string;
  feature: string;
}

/**
 * The figures behind a server-limit refusal, or null when they did not arrive.
 *
 * Null is a real outcome rather than a defensive gesture: a caller that gets it
 * still has `caught.message`, which is the same sentence the API wrote, and
 * rendering that is strictly better than rendering "0 of 0 servers" assembled
 * from defaults.
 */
export function serverLimitReached(caught: unknown): ServerLimitReached | null {
  const extensions = planProblem(caught, SERVER_LIMIT_REACHED);
  if (!extensions) {
    return null;
  }

  const { plan, limit, used } = extensions;

  return typeof plan === "string" && typeof limit === "number" && typeof used === "number"
    ? { plan, limit, used }
    : null;
}

/** The plan and capability behind a feature refusal, or null when they did not arrive. */
export function featureNotInPlan(caught: unknown): FeatureNotInPlan | null {
  const extensions = planProblem(caught, FEATURE_NOT_IN_PLAN);
  if (!extensions) {
    return null;
  }

  const { plan, feature } = extensions;

  return typeof plan === "string" && typeof feature === "string" ? { plan, feature } : null;
}

function planProblem(
  caught: unknown,
  type: string,
): Readonly<Record<string, unknown>> | null {
  if (!isPlanRefusal(caught) || caught.problemType !== type) {
    return null;
  }

  return caught.extensions ?? null;
}

/**
 * Reads the antiforgery token the API sets alongside the session.
 *
 * Deliberately a readable cookie, unlike the session. That is the whole
 * double-submit mechanism: an attacker on another origin can cause the browser
 * to send the session cookie but cannot read this one to echo it back.
 */
function csrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)__Secure-sudo\.csrf=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");

  const method = (init.method ?? "GET").toUpperCase();
  const mutating = method !== "GET" && method !== "HEAD";

  if (mutating) {
    headers.set("Content-Type", "application/json");

    const token = csrfToken();
    if (token) {
      headers.set("X-XSRF-TOKEN", token);
    }
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    method,
    headers,
    // The session cookie is scoped to .sudogamesservers.co.uk, so app. and api.
    // are same-site and it travels without a CORS preflight.
    credentials: "include",
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const body = await response.text();
  const parsed = body ? safeParse(body) : null;

  if (!response.ok) {
    throw toApiError(response.status, parsed);
  }

  return parsed as T;
}

/**
 * Standard RFC 9457 members. Everything else in a problem document is an
 * extension, which is exactly where the entitlement refusals put their figures.
 */
const PROBLEM_MEMBERS = new Set(["type", "title", "status", "detail", "instance", "errors"]);

/**
 * Turns a failure body into an ApiError without flattening what it said.
 *
 * Two shapes arrive here. Almost everything answers with `{ "error": "..." }`;
 * the entitlement refusals answer with `application/problem+json`, which carries
 * a stable `type` URI and the figures as extension members. Keeping only the
 * status and the sentence would leave a client unable to tell the two 402s apart
 * and force any view wanting the numbers to parse them back out of English, so
 * both are carried through.
 */
function toApiError(status: number, parsed: any): ApiError {
  // `detail` sits between the two on purpose: it is the problem document's own
  // prose, and the API sends the same sentence in `error` today. Reading `title`
  // first would render "Server limit reached" where a whole explanation exists.
  const message = parsed?.error ?? parsed?.detail ?? parsed?.title ?? `Request failed (${status})`;

  // A problem document identifies itself with a `type` URI. Anything without one
  // is the `{ error }` shape and has no extensions to read.
  const problemType = typeof parsed?.type === "string" ? parsed.type : undefined;

  return new ApiError(
    status,
    message,
    parsed?.errors,
    problemType,
    problemType ? extensionsOf(parsed) : undefined,
  );
}

function extensionsOf(parsed: Record<string, unknown>): Record<string, unknown> {
  const extensions: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(parsed)) {
    if (!PROBLEM_MEMBERS.has(key)) {
      extensions[key] = value;
    }
  }

  return extensions;
}

function safeParse(body: string): any {
  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
}

/**
 * Renders a query string, or nothing at all when no parameter was set.
 *
 * A bare trailing `?` is harmless to the router but shows up in the address bar
 * and in logs, which makes an unfiltered request look like a filtered one.
 */
function queryString(query: URLSearchParams): string {
  const rendered = query.toString();
  return rendered ? `?${rendered}` : "";
}

export interface Game {
  code: string;
  name: string;
  available: boolean;
}

/**
 * One server.
 *
 * `archivedAt` non-null means it has been taken out of service: its key is
 * revoked, it counts against nothing, and it no longer appears in the list or in
 * any fleet figure. Its own page still opens, because its history is still there,
 * so a view that shows one has to say so rather than render it as offline.
 * `status` is "Archived" in that case, ahead of any staleness wording.
 */
export interface Server {
  id: string;
  name: string;
  game: string;
  /**
   * The mode of the connection this server was added with.
   *
   * Kept because the API still sends it, and read by nothing here. A server has
   * a set of connections rather than one, so this field cannot describe a server
   * with both the mod and RCON — it names the first one and silently omits the
   * rest. Read `connectionModes`, or on the detail response `connections`, which
   * is the only one of the three that carries an endpoint.
   *
   * It reads "None" only for a server with no connections at all, which no route
   * produces: creating a server attaches its mod connection in the same act, and
   * the connections endpoints take RCON alone — every other mode is refused with
   * 409, the mod's saying it goes with the server's key rather than by hand. So
   * "None" is a value to render rather than a state to design around, and a
   * server that has had RCON removed still has the mod.
   */
  connectionMode: string;
  /** Every configured mode, in the order they were configured. */
  connectionModes: string[];
  status: string;
  currentPlayers: number;
  maxPlayers: number | null;
  mapName: string | null;
  gameVersion: string | null;
  lastSeenAt: string | null;
  linked: boolean;
  archivedAt: string | null;
}

/**
 * One configured way of reaching a server.
 *
 * `mode` is a name — "Mod", "Rcon", "Provider" or "Agent" — never a number, so a
 * renumbered enum cannot change what a stored value means. Typed as a string
 * rather than a union so a mode added by a newer API renders as itself instead
 * of failing to compile.
 *
 * `capabilities` is what this one connection contributes, which is what lets a
 * view answer "why is the ban button here" with "because RCON is configured".
 * The server's own capability list is the union of these and is the thing a
 * control should be rendered from.
 *
 * `host` and `port` are null on every mod connection by design: the mod dials
 * out to us rather than the other way round.
 */
export interface ServerConnection {
  id: string;
  mode: string;
  host: string | null;
  port: number | null;
  configuredAt: string;
  capabilities: string[];
}

/**
 * The connections on a server and what they add up to.
 *
 * Returned by attach and by remove, so a view can re-render the list and the
 * capability union from one response. Fetching the detail again instead would
 * leave a window where the two disagree on screen.
 */
export interface ServerConnections {
  connections: ServerConnection[];
  capabilities: string[];
}

export interface CreatedServer {
  server: Server;
  /** Returned exactly once, at creation. Never retrievable again. */
  apiKey: string;
  configJson: string;
  workshopUrl: string;
}

/**
 * One stored event.
 *
 * `payload` is JSON rather than a string, so nothing here has to parse a string
 * out of an already-parsed document. It is `unknown` because the shape varies by
 * `type` and the API makes no promise about it; a view that reads a field has to
 * narrow first.
 */
export interface ServerEvent {
  id: number;
  serverId: string;
  serverName: string;
  type: string;
  occurredAt: string;
  receivedAt: string;
  payload: unknown;
}

/**
 * A window onto the append-only event log, newest first.
 *
 * `hasMore` on a poll that sent `after` means more arrived than one page holds,
 * so advancing the cursor to `highestId` would skip the events in between. The
 * honest recovery is a full refresh, not a cursor bump.
 */
export interface ServerEventPage {
  events: ServerEvent[];
  highestId: number;
  hasMore: boolean;
}

/**
 * Everything the server drill-down needs in one response.
 *
 * `capabilities` is the union of what the `connections` provide, recomputed by
 * the API whenever one is attached or removed. It is what a control is rendered
 * from — never the connection type, which is the assumption this whole shape
 * exists to remove.
 */
export interface ServerDetail {
  server: Server;
  capabilities: string[];
  connections: ServerConnection[];
  modVersion: string | null;
  linkedAt: string | null;
  onlinePlayers: number;
  playersSeenLastDay: number;
  sessionsLastDay: number;
  lastEventAt: string | null;
}

export interface RosterPlayer {
  playerId: string;
  displayName: string;
  steam64: string | null;
  battlEyeGuid: string | null;
  connectedAt: string;
  connectedSeconds: number;
}

/**
 * Who is on a server.
 *
 * `live` false means these rows are the last roster we were told about rather
 * than the current one, and the view is expected to say so on screen.
 *
 * `staleReason` names which condition failed and is null when `live` is true.
 * It matters because `status` and `live` are answers to different questions: a
 * server heartbeating without a mod session is genuinely Online and genuinely
 * not live, so the view has to read this rather than assume the server has gone
 * away. Typed as a string rather than a union so a reason added by a newer API
 * falls through to the general wording instead of failing to compile.
 */
export interface Roster {
  serverId: string;
  status: string;
  live: boolean;
  staleReason: string | null;
  lastSeenAt: string | null;
  players: RosterPlayer[];
}

export interface FleetSummary {
  servers: number;
  online: number;
  offline: number;
  awaitingFirstConnection: number;
  currentPlayers: number;
  capacity: number | null;
}

export interface PlayerSummary {
  online: number;
  known: number;
  seenLastDay: number;
  sessionsLastDay: number;
}

export interface IngestSummary {
  lastEventAt: string | null;
  eventsThisRun: number;
  projectionCaughtUp: boolean;
}

export interface Overview {
  fleet: FleetSummary;
  players: PlayerSummary;
  ingest: IngestSummary;
  servers: Server[];
  recentEvents: ServerEvent[];
}

export interface PlayerIdentifier {
  kind: string;
  value: string;
  source: string;
  confidence: string;
  firstSeenAt: string;
  lastSeenAt: string;
  retiredAt: string | null;
}

export interface PlayerAlias {
  name: string;
  firstSeenAt: string;
  lastSeenAt: string;
  seenCount: number;
}

export interface PlayerSession {
  serverId: string;
  serverName: string;
  connectedAt: string;
  disconnectedAt: string | null;
  /** Present only where the game told us the player left. */
  durationSeconds: number | null;
  elapsedSeconds: number;
  endedReason: string;
  live: boolean;
}

export interface PlayerListItem {
  playerId: string;
  displayName: string;
  steam64: string | null;
  battlEyeGuid: string | null;
  aliases: string[];
  firstSeenAt: string;
  lastSeenAt: string;
  totalSessions: number;
  onServerId: string | null;
  onServerName: string | null;
}

export interface PlayerPage {
  players: PlayerListItem[];
  hasMore: boolean;
}

export interface PlayerDetail {
  playerId: string;
  displayName: string;
  firstSeenAt: string;
  lastSeenAt: string;
  totalSessions: number;
  totalPlaytimeSeconds: number;
  onServerId: string | null;
  onServerName: string | null;
  identifiers: PlayerIdentifier[];
  aliases: PlayerAlias[];
  sessions: PlayerSession[];
}

/**
 * The three installation decisions, which differ only in the verb.
 *
 * One helper rather than three near-copies: the parts that must not diverge —
 * the path, the method and the response shape — are the parts that are
 * identical, and the verb is the only thing that changes.
 */
function decideAgent(orgSlug: string, installationId: string, decision: string) {
  return request<AgentInstallation>(
    `/v1/orgs/${encodeURIComponent(orgSlug)}/agents/${encodeURIComponent(installationId)}/${decision}`,
    { method: "POST" },
  );
}

/**
 * One price, in the currency's minor unit.
 *
 * `amountMinor` is an integer all the way from the database, because a price
 * divided on the way out is a price that disagrees with the invoice by a
 * rounding error. Dividing it happens once, at the point of display.
 */
export interface PlanPrice {
  currency: string;
  amountMinor: number;
  /** "Monthly" or "Yearly" — the enum's name, never its number. */
  interval: string;
}

/**
 * A tier as offered.
 *
 * `current` is decided by the API rather than by comparing codes here, so the
 * pricing table cannot disagree with the entitlement it was served alongside.
 * `serverLimit` null means unlimited, and must render as that rather than as 0.
 */
export interface PlanOption {
  code: string;
  name: string;
  description: string;
  serverLimit: number | null;
  retentionDays: number;
  features: string[];
  prices: PlanPrice[];
  current: boolean;
}

/** What the organisation is using against what it is allowed. */
export interface PlanUsage {
  servers: number;
  serverLimit: number | null;
  /** Null on an unlimited plan. Zero means the next create is refused with 402. */
  serversRemaining: number | null;
}

export interface OrgPlan {
  planCode: string;
  planName: string;
  /** "Default", "Operator" or "Provider" — why this organisation is on this tier. */
  source: string;
  since: string | null;
  serverLimit: number | null;
  retentionDays: number;
  features: string[];
  usage: PlanUsage;
  plans: PlanOption[];
}

/**
 * One published agent build.
 *
 * Read by the portal only to answer "is there anything to install yet"; the
 * signature and digest are the agent's business, not a browser's.
 */
export interface AgentRelease {
  version: string;
  /**
   * "stable" or "beta", lowercase — this is the wire name that appears in the
   * signed manifest, not the enum's name. Compare it case-insensitively.
   */
  channel: string;
  /** A runtime identifier, such as "linux-x64". */
  platform: string;
  sizeBytes: number;
  sha256: string;
  signature: string;
  signingKeyId: string;
  downloadUrl: string;
  publishedAt: string;
}

/**
 * A machine that has enrolled.
 *
 * `publicKeyFingerprint` is carried in full deliberately: it is the only thing
 * on an approval screen an owner can check independently, by comparing it with
 * what the agent printed on the machine.
 */
export interface AgentInstallation {
  id: string;
  machineName: string;
  platform: string | null;
  agentVersion: string | null;
  /** "PendingApproval", "Approved", "Rejected" or "Revoked". */
  status: string;
  publicKeyFingerprint: string;
  enrolledAt: string;
  decidedAt: string | null;
}

/**
 * An enrolment token as the list shows it.
 *
 * `state` is derived server-side so that four views of the same list cannot
 * disagree about a token in its last second. Never carries the secret.
 */
export interface EnrolmentToken {
  id: string;
  tokenId: string;
  label: string;
  /** "Pending", "Used", "Expired", "Revoked" or "Locked". */
  state: string;
  createdAt: string;
  expiresAt: string;
  consumedAt: string | null;
}

/** Returned exactly once, at creation. Stored hashed, so it cannot be shown again. */
export interface MintedEnrolmentToken {
  id: string;
  tokenId: string;
  token: string;
  expiresAt: string;
  note: string;
}

export const api = {
  me: () => request<Me>("/auth/v1/me"),

  games: (orgSlug: string) =>
    request<Game[]>(`/v1/orgs/${encodeURIComponent(orgSlug)}/servers/games`),

  servers: (orgSlug: string) =>
    request<Server[]>(`/v1/orgs/${encodeURIComponent(orgSlug)}/servers/`),

  createServer: (orgSlug: string, game: string, name: string) =>
    request<CreatedServer>(`/v1/orgs/${encodeURIComponent(orgSlug)}/servers/`, {
      method: "POST",
      body: JSON.stringify({ game, name }),
    }),

  rotateServerKey: (orgSlug: string, serverId: string) =>
    request<CreatedServer>(
      `/v1/orgs/${encodeURIComponent(orgSlug)}/servers/${serverId}/keys`,
      { method: "POST" },
    ),

  // The only self-serve way out of a full plan, and the reason the 402 is allowed
  // to mention archiving at all. Idempotent server-side, so a second click is a
  // repeat rather than a failure. Needs `server.delete`.
  archiveServer: (orgSlug: string, serverId: string) =>
    request<Server>(
      `/v1/orgs/${encodeURIComponent(orgSlug)}/servers/${encodeURIComponent(serverId)}/archive`,
      { method: "POST" },
    ),

  // Additive: attaching RCON to a server that already has the mod leaves the mod
  // alone and adds what RCON can do to what the mod can do. There is no
  // entitlement behind this and there must not be one — RCON is on every plan,
  // including Free, so nothing here should ever be hidden or gated on a plan.
  // Needs `server.create`. 409 if the server is archived or already has that
  // mode; the response carries the recomputed capability union alongside the
  // list, so both re-render from one answer.
  attachConnection: (
    orgSlug: string,
    serverId: string,
    mode: string,
    host: string,
    port: number,
  ) =>
    request<ServerConnections>(
      `/v1/orgs/${encodeURIComponent(orgSlug)}/servers/${encodeURIComponent(serverId)}/connections`,
      { method: "POST", body: JSON.stringify({ mode, host, port }) },
    ),

  // Idempotent server-side, so a second click is a repeat rather than a failure.
  // Takes back only what that connection contributed: removing RCON from a
  // server that also has the mod costs it banning and leaves the mod's events,
  // kicks and messages exactly as they were. Needs `server.delete`.
  removeConnection: (orgSlug: string, serverId: string, mode: string) =>
    request<ServerConnections>(
      `/v1/orgs/${encodeURIComponent(orgSlug)}/servers/${encodeURIComponent(serverId)}` +
        `/connections/${encodeURIComponent(mode)}`,
      { method: "DELETE" },
    ),

  overview: (orgSlug: string) =>
    request<Overview>(`/v1/orgs/${encodeURIComponent(orgSlug)}/overview`),

  server: (orgSlug: string, serverId: string) =>
    request<ServerDetail>(
      `/v1/orgs/${encodeURIComponent(orgSlug)}/servers/${encodeURIComponent(serverId)}`,
    ),

  roster: (orgSlug: string, serverId: string) =>
    request<Roster>(
      `/v1/orgs/${encodeURIComponent(orgSlug)}/servers/${encodeURIComponent(serverId)}/players`,
    ),

  // `after` is a keyset cursor on the event id, not an offset. Offset paging
  // double-counts under concurrent inserts, which is the one thing an
  // append-only feed does constantly.
  serverEvents: (orgSlug: string, serverId: string, after?: number, limit?: number) => {
    const query = new URLSearchParams();
    if (after !== undefined && after > 0) {
      query.set("after", String(after));
    }
    if (limit !== undefined) {
      query.set("limit", String(limit));
    }
    const suffix = queryString(query);

    return request<ServerEventPage>(
      `/v1/orgs/${encodeURIComponent(orgSlug)}/servers/${encodeURIComponent(serverId)}/events${suffix}`,
    );
  },

  players: (orgSlug: string, q?: string, limit?: number) => {
    const query = new URLSearchParams();
    if (q) {
      query.set("q", q);
    }
    if (limit !== undefined) {
      query.set("limit", String(limit));
    }
    const suffix = queryString(query);

    return request<PlayerPage>(`/v1/orgs/${encodeURIComponent(orgSlug)}/players/${suffix}`);
  },

  // Keyed by Steam64 rather than by the internal identity id, because the thing
  // an operator has in front of them is the identifier a player pasted into a
  // report.
  player: (orgSlug: string, steam64: string) =>
    request<PlayerDetail>(
      `/v1/orgs/${encodeURIComponent(orgSlug)}/players/${encodeURIComponent(steam64)}`,
    ),

  // The plan, its limits, live usage and the whole public catalogue in one
  // response. One call rather than three, because "1 of 1 servers used" and
  // "what upgrading unlocks" are the same sentence on the page and separate
  // calls could disagree by however long the slower one took.
  plan: (orgSlug: string) => request<OrgPlan>(`/v1/orgs/${encodeURIComponent(orgSlug)}/plan`),

  // Anonymous and not organisation-scoped: the manifest is the same for every
  // reader. Asked here for one reason only — whether a build exists to install.
  agentReleases: () => request<AgentRelease[]>("/agent/v1/releases"),

  agents: (orgSlug: string) =>
    request<AgentInstallation[]>(`/v1/orgs/${encodeURIComponent(orgSlug)}/agents/`),

  agentTokens: (orgSlug: string) =>
    request<EnrolmentToken[]>(`/v1/orgs/${encodeURIComponent(orgSlug)}/agents/tokens`),

  mintAgentToken: (orgSlug: string, label: string) =>
    request<MintedEnrolmentToken>(`/v1/orgs/${encodeURIComponent(orgSlug)}/agents/tokens`, {
      method: "POST",
      body: JSON.stringify({ label }),
    }),

  revokeAgentToken: (orgSlug: string, tokenId: string) =>
    request<EnrolmentToken>(
      `/v1/orgs/${encodeURIComponent(orgSlug)}/agents/tokens/${encodeURIComponent(tokenId)}`,
      { method: "DELETE" },
    ),

  // Owner only, server-side. The portal hides these on role, which is cosmetic;
  // the API is what actually refuses.
  approveAgent: (orgSlug: string, installationId: string) =>
    decideAgent(orgSlug, installationId, "approve"),

  rejectAgent: (orgSlug: string, installationId: string) =>
    decideAgent(orgSlug, installationId, "reject"),

  revokeAgent: (orgSlug: string, installationId: string) =>
    decideAgent(orgSlug, installationId, "revoke"),

  login: (email: string, password: string) =>
    request<Me>("/auth/v1/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  register: (email: string, password: string, displayName: string, organisationName: string) =>
    request<Me>("/auth/v1/register", {
      method: "POST",
      body: JSON.stringify({ email, password, displayName, organisationName }),
    }),

  logout: () => request<{ ok: boolean }>("/auth/v1/logout", { method: "POST" }),

  live: () => request<HealthReport>("/health/live"),
  ready: () => request<HealthReport>("/health/ready"),
  dayz: () => request<HealthReport>("/dayz/health"),
};

export { API_BASE };
